"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { getLink, Routes } from "@/constants";
import { Translations } from "@/translations";
import classroomPreview from "../../../public/assets/img/chess/classroom-chess-item.png";
import quizPreview from "../../../public/assets/img/chess/chess-quiz.png";
import styles from "./learning-tools.module.scss";

interface LearningToolsMainProps {
  locale: string;
  page?: "classrooms" | "quizzes";
}

const subjects = [
  { key: "TimesTables", path: "times-table", symbol: "X" },
  { key: "Addition", path: "addition-facts", symbol: "+" },
  { key: "Subtraction", path: "subtraction-facts", symbol: "-" },
  { key: "Division", path: "division-facts", symbol: "/" },
  { key: "Alphabet", path: "alphabet", symbol: "ABC" },
  { key: "Nato", path: "nato-alphabet", symbol: "A-Z" },
] as const;

const benefitIcons = ["collection", "lightbulb", "people"] as const;
const modeIcons = ["layers", "patch-question", "stopwatch"] as const;

export default function LearningToolsMain({ locale, page = "classrooms" }: LearningToolsMainProps) {
  const t = useTranslations(Translations.LearningTools.Page);
  const source = useSearchParams().get("source")?.trim();
  const tracked = (href: string) => source
    ? `${href}?${new URLSearchParams({ source }).toString()}`
    : href;
  const classroomsUrl = tracked(getLink(locale, Routes.LEARNING_TOOLS));
  const quizzesUrl = tracked(getLink(locale, Routes.LEARNING_TOOLS_QUIZZES));
  const catalogueUrl = tracked(getLink(locale, Routes.QUIZZES));
  const plansUrl = tracked(getLink(locale, Routes.CLASSROOM_FEATURES));
  const signupUrl = tracked(getLink(locale, Routes.SIGN_UP));
  const isQuizzes = page === "quizzes";
  const prefix = isQuizzes ? "Quiz" : "Classroom";
  const BenefitHeading = isQuizzes ? "h3" : "h2";

  return (
    <div className={`container ${styles.page}`}>
      <nav className={styles.navigation} aria-label={t("NavigationLabel")}>
        <Link href={classroomsUrl} aria-current={!isQuizzes ? "page" : undefined}>
          {t("Classrooms")}
        </Link>
        <Link href={quizzesUrl} aria-current={isQuizzes ? "page" : undefined}>
          {t("Quizzes")}
        </Link>
      </nav>

      <header className={styles.hero}>
        <div>
          <span className={styles.eyebrow}>{t("Eyebrow")}</span>
          <h1>{t(`${prefix}HeroTitle`)}</h1>
          <p>{t(`${prefix}HeroDescription`)}</p>
          <div className={styles.actions}>
            <Link href={isQuizzes ? catalogueUrl : signupUrl} className="e-btn e-btn-theme">
              {t(isQuizzes ? "ExploreQuizzes" : "TryClassrooms")}
            </Link>
            <Link href={isQuizzes ? signupUrl : plansUrl} className="e-btn e-btn-border">
              {t(isQuizzes ? "TryCreating" : "ViewPlans")}
            </Link>
          </div>
        </div>
        <figure className={styles.preview}>
          <Image
            src={isQuizzes ? quizPreview : classroomPreview}
            alt={t(`${prefix}ImageAlt`)}
            sizes="(max-width: 991px) 100vw, 50vw"
            priority
          />
          <figcaption>{t(`${prefix}ImageCaption`)}</figcaption>
        </figure>
      </header>

      <section className={styles.section} aria-labelledby={isQuizzes ? "benefits-title" : undefined}>
        {isQuizzes && (
          <div className={styles.intro}>
            <h2 id="benefits-title">{t("QuizBenefitsTitle")}</h2>
            <p>{t("QuizBenefitsDescription")}</p>
          </div>
        )}
        <div className={styles.grid}>
          {benefitIcons.map((icon, index) => (
            <article key={icon} className={styles.card}>
              <span className={styles.icon} aria-hidden="true"><i className={`bi bi-${icon}`} /></span>
              <BenefitHeading>{t(`${prefix}Benefit${index + 1}Title`)}</BenefitHeading>
              <p>{t(`${prefix}Benefit${index + 1}Description`)}</p>
            </article>
          ))}
        </div>
        {isQuizzes && <p className={styles.note}>{t("FormatNote")}</p>}
      </section>

      {isQuizzes ? (
        <>
          <section className={styles.section} aria-labelledby="practice-title">
            <div className={styles.intro}>
              <h2 id="practice-title">{t("ModesTitle")}</h2>
              <p>{t("ModesDescription")}</p>
            </div>
            <div className={styles.grid}>
              {modeIcons.map((icon, index) => (
                <article key={icon} className={styles.card}>
                  <span className={styles.icon} aria-hidden="true"><i className={`bi bi-${icon}`} /></span>
                  <h3>{t(`Mode${index + 1}Title`)}</h3>
                  <p>{t(`Mode${index + 1}Description`)}</p>
                </article>
              ))}
            </div>
          </section>

          <section
            id="subject-collections"
            className={`${styles.section} ${styles.collections}`}
            aria-labelledby="collections-title"
          >
            <div className={styles.intro}>
              <h2 id="collections-title">{t("CollectionsTitle")}</h2>
              <p>{t("CollectionsDescription")}</p>
            </div>
            <div className={styles.grid}>
              {subjects.map(({ key, path, symbol }) => (
                <article key={key} className={styles.card}>
                  <span className={styles.icon} aria-hidden="true">{symbol}</span>
                  <h3>{t(`${key}Title`)}</h3>
                  <p>{t(`${key}Description`)}</p>
                  <Link href={tracked(`/${locale}/features-hub/${path}`)}>
                    {t("ExploreSubject", { subject: t(`${key}Title`) })}
                  </Link>
                </article>
              ))}
            </div>
            <p className={styles.note}>{t("CollectionsNote")}</p>
          </section>
        </>
      ) : (
        <>
          <section className={styles.section} aria-labelledby="journey-title">
            <div className={styles.intro}>
              <h2 id="journey-title">{t("JourneyTitle")}</h2>
              <p>{t("JourneyDescription")}</p>
            </div>
            <ol className={`${styles.grid} ${styles.steps}`}>
              {[1, 2, 3].map((step) => (
                <li key={step} className={styles.card}>
                  <span className={styles.icon} aria-hidden="true">{step}</span>
                  <h3>{t(`Step${step}Title`)}</h3>
                  <p>{t(`Step${step}Description`)}</p>
                </li>
              ))}
            </ol>
          </section>

          <section className={`${styles.section} ${styles.teaser}`} aria-labelledby="subject-teaser-title">
            <h2 id="subject-teaser-title">{t("SubjectTeaserTitle")}</h2>
            <p>{t("SubjectTeaserDescription")}</p>
            <div className={styles.actions}>
              <Link href={`${quizzesUrl}#subject-collections`} className="e-btn e-btn-border">
                {t("ExploreSubjects")}
              </Link>
            </div>
          </section>
        </>
      )}

      <section className={styles.closing} aria-labelledby="get-started-title">
        <h2 id="get-started-title">{t(`${prefix}ClosingTitle`)}</h2>
        <p>{t(`${prefix}ClosingDescription`)}</p>
        <div className={styles.actions}>
          <Link href={signupUrl} className="e-btn e-btn-theme">
            {t(isQuizzes ? "TryCreating" : "TryClassrooms")}
          </Link>
          <Link href={isQuizzes ? catalogueUrl : plansUrl} className="e-btn e-btn-border">
            {t(isQuizzes ? "ExploreQuizzes" : "ViewPlans")}
          </Link>
          <Link href={isQuizzes ? classroomsUrl : quizzesUrl} className="e-btn e-btn-border">
            {t(isQuizzes ? "ExploreClassrooms" : "DiscoverQuizzes")}
          </Link>
        </div>
      </section>
    </div>
  );
}
